import type { Identifier } from "ra-core";

/**
 * Shared contract for uploading a developer's Constancia de Situacion Fiscal
 * (Almikia Epic E1b, TASK-027/028/033). Both the Supabase and FakeRest
 * providers upload to the same bucket id, enforce the same 10 MB / PDF-only
 * limit, and hand out signed URLs with the same short expiry.
 */

/** Private storage bucket id for Constancia uploads (see supabase/schemas/07_storage.sql). */
export const DEVELOPER_FISCAL_DOCUMENTS_BUCKET = "developer-fiscal-documents";

/** Maximum accepted Constancia file size, in bytes (10 MiB). */
export const FISCAL_DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Signed URL expiry for a Constancia download, in seconds. */
export const FISCAL_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS = 60;

const FISCAL_DOCUMENT_MIME_TYPE = "application/pdf";

/**
 * Reason code for a rejected upload. TASK-035 owns the Spanish label for
 * each code; this module only decides which one applies.
 */
export type FiscalDocumentRejectionReason = "invalid_type" | "too_large";

export type FiscalDocumentValidationResult =
  | { valid: true }
  | { valid: false; reason: FiscalDocumentRejectionReason };

/**
 * Validates a candidate Constancia upload before any network/storage call:
 * it must be a PDF and no larger than {@link FISCAL_DOCUMENT_MAX_SIZE_BYTES}.
 */
export function validateFiscalDocumentFile(
  file: File,
): FiscalDocumentValidationResult {
  if (file.type !== FISCAL_DOCUMENT_MIME_TYPE) {
    return { valid: false, reason: "invalid_type" };
  }
  if (file.size > FISCAL_DOCUMENT_MAX_SIZE_BYTES) {
    return { valid: false, reason: "too_large" };
  }
  return { valid: true };
}

/**
 * Computes the lowercase-hex SHA-256 digest of a file using the Web Crypto
 * API (`crypto.subtle`) -- no hashing dependency added.
 *
 * ASSUMPTION: this hash is computed client-side and is advisory only (a
 * corrupted/tampered upload could bypass it); E5 may recompute it
 * server-side once the storage migration lands.
 */
export async function computeFileSha256Hex(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Builds a fresh, unique storage path for a developer's Constancia upload.
 *
 * ASSUMPTION: the path never carries the RFC, razon social, or the user's
 * original file name -- only the developer id and a random id -- so the
 * storage path itself never leaks fiscal PII, per the ticket's requirement.
 */
export function buildFiscalDocumentStoragePath(
  developerId: Identifier,
): string {
  return `${developerId}/${crypto.randomUUID()}.pdf`;
}
